// One component, two shapes: an HTML <table> at md+, and per-row cards via
// the caller's renderCard at <md (so a phone never horizontal-scrolls a wide
// table). The wrapper handles row click, highlight, empty, and loading states.

import type { ReactNode } from 'react';
import { Link } from 'react-router';
import { cn } from '@/lib/classnames';
import { Skeleton } from './index';

export interface Column<T> {
  key: string;
  header: ReactNode;
  cell: (row: T) => ReactNode;
  align?: 'left' | 'right';
  // Optional td className applied per-cell, in addition to the default
  // padding. Use for tabular-nums, monospace, etc.
  cellClassName?: string;
  // Optional th className for header alignment / tooltip-only headers.
  headerClassName?: string;
  // Hide this column below the given breakpoint (Tailwind responsive prefix).
  hideBelow?: 'sm' | 'md' | 'lg' | 'xl' | '2xl';
}

interface ResponsiveTableProps<T> {
  columns: ReadonlyArray<Column<T>>;
  rows: ReadonlyArray<T>;
  rowKey: (row: T) => string | number;
  // <md card body. Total layout freedom - wrap in the card chrome / link
  // / row-flash automatically applied by this component.
  renderCard: (row: T) => ReactNode;

  // Row click handlers. If both are provided, href wins (it's the more
  // accessible affordance - middle-click, copy-link, etc.).
  rowHref?: (row: T) => string;
  onRowClick?: (row: T) => void;

  // Optional per-row className - added to <tr> (table) and to the
  // <article> (card). Used for live-state highlights / opacity / flash.
  rowClassName?: (row: T) => string | undefined;

  // Called on mouseEnter for a row - use to prefetch detail data.
  onRowHover?: (row: T) => void;

  loading?: boolean;
  // How many skeleton rows / cards to render while loading. Defaults to 3.
  skeletonRows?: number;
  // Slotted empty state. Rendered in a single-cell row spanning all
  // columns at md+, and as a centered block at <md.
  emptyState?: ReactNode;

  className?: string;
}

const HIDE_BELOW_MAP: Record<NonNullable<Column<unknown>['hideBelow']>, string> = {
  sm: 'hidden sm:table-cell',
  md: 'hidden md:table-cell',
  lg: 'hidden lg:table-cell',
  xl: 'hidden xl:table-cell',
  '2xl': 'hidden 2xl:table-cell',
};

export function ResponsiveTable<T>({
  columns,
  rows,
  rowKey,
  renderCard,
  rowHref,
  onRowClick,
  rowClassName,
  onRowHover,
  loading,
  skeletonRows = 3,
  emptyState,
  className,
}: ResponsiveTableProps<T>) {
  const isEmpty = !loading && rows.length === 0;
  return (
    <>
      {/* ── md+ table view ─────────────────────────────────────────── */}
      <div className={cn('hidden md:block card table-wrapper', className)}>
        <table className="data-table">
          <thead>
            <tr>
              {columns.map((c) => (
                <th
                  key={c.key}
                  className={cn(
                    c.align === 'right' ? 'text-right' : 'text-left',
                    c.hideBelow && HIDE_BELOW_MAP[c.hideBelow],
                    c.headerClassName
                  )}
                >
                  {c.header}
                </th>
              ))}
            </tr>
          </thead>
          {/* Key by load phase so rows remount (and fade in) when the
              skeleton is replaced; background refetches keep loading=false,
              so it animates once, not on every poll. */}
          <tbody key={loading ? 'sk' : 'rows'} className={cn(!loading && 'animate-fade-in')}>
            {loading &&
              Array.from({ length: skeletonRows }).map((_, i) => (
                <tr key={`sk-${i}`}>
                  {columns.map((c) => (
                    <td
                      key={c.key}
                      className={cn(
                        c.hideBelow && HIDE_BELOW_MAP[c.hideBelow]
                      )}
                    >
                      <Skeleton height={16} className="w-3/4" />
                    </td>
                  ))}
                </tr>
              ))}
            {isEmpty && emptyState && (
              <tr>
                <td colSpan={columns.length}>{emptyState}</td>
              </tr>
            )}
            {!loading &&
              rows.map((row) => {
                const key = rowKey(row);
                const cls = rowClassName?.(row);
                const cellContent = columns.map((c, ci) => (
                  <td
                    key={c.key}
                    className={cn(
                      c.align === 'right' ? 'text-right' : 'text-left',
                      c.hideBelow && HIDE_BELOW_MAP[c.hideBelow],
                      c.cellClassName
                    )}
                  >
                    {c.cell(row)}
                    {/* Row anchor rides in the first cell rather than a cell of
                        its own - an extra <td> gave every body row one more
                        column than the header, which screen readers announce
                        as a phantom column. */}
                    {rowHref && ci === 0 && (
                      <Link to={rowHref(row)} data-row-anchor className="sr-only">
                        Open row {String(key)}
                      </Link>
                    )}
                  </td>
                ));
                if (rowHref) {
                  // A hidden Link anchors the row so middle-click / copy-link
                  // work; the <tr> onClick mirrors it for clicks anywhere.
                  return (
                    <tr
                      key={key}
                      className={cn(
                        'cursor-pointer',
                        cls
                      )}
                      onMouseEnter={onRowHover ? () => onRowHover(row) : undefined}
                      onClick={(e) => {
                        // Don't hijack clicks on a real control inside the cell.
                        if ((e.target as HTMLElement).closest('a,button,input,select,textarea,label')) return;
                        // Navigate by synthesizing a click on the hidden anchor.
                        const a = (e.currentTarget as HTMLElement).querySelector<HTMLAnchorElement>('a[data-row-anchor]');
                        a?.click();
                      }}
                    >
                      {cellContent}
                    </tr>
                  );
                }
                return (
                  <tr
                    key={key}
                    className={cn(
                      onRowClick && 'cursor-pointer',
                      cls
                    )}
                    onClick={onRowClick ? () => onRowClick(row) : undefined}
                  >
                    {cellContent}
                  </tr>
                );
              })}
          </tbody>
        </table>
      </div>

      {/* ── <md card view ──────────────────────────────────────────── */}
      <div key={loading ? 'sk' : 'rows'} className={cn('md:hidden space-y-2.5', !loading && 'animate-fade-in', className)}>
        {loading &&
          Array.from({ length: skeletonRows }).map((_, i) => (
            <div key={`sk-${i}`} className="card" style={{ padding: '16px' }}>
              <Skeleton height={18} className="w-2/3 mb-2" />
              <Skeleton height={12} className="w-1/2" />
            </div>
          ))}
        {isEmpty && (
          <div className="card">{emptyState}</div>
        )}
        {!loading &&
          rows.map((row) => {
            const cls = rowClassName?.(row);
            const card = (
              <article
                className={cn(
                  'card transition-colors',
                  (rowHref || onRowClick) && 'hover:bg-bg-app active:bg-bg-app',
                  cls
                )}
                style={{ padding: '16px' }}
              >
                {renderCard(row)}
              </article>
            );
            if (rowHref) {
              return (
                <Link key={rowKey(row)} to={rowHref(row)} className="block" onMouseEnter={onRowHover ? () => onRowHover(row) : undefined}>
                  {card}
                </Link>
              );
            }
            if (onRowClick) {
              return (
                <button
                  key={rowKey(row)}
                  type="button"
                  onClick={() => onRowClick(row)}
                  className="block w-full text-left"
                >
                  {card}
                </button>
              );
            }
            return <div key={rowKey(row)}>{card}</div>;
          })}
      </div>
    </>
  );
}
